import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { BrowserRouter, Routes, Route, useNavigate } from "react-router-dom";
import {
  MapPin, Calendar, Users, Shield, Star, Check, Trash2, X,
  Award, Heart, Phone, User, LogOut, TrendingUp, Clock, Globe,
  Mountain, ChevronDown, ChevronRight, ChevronLeft, Mail
} from "lucide-react";
import axios from "axios";
import { Toaster, toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const API = `${BACKEND_URL}/api`;

// ─── INDIA STATES CATALOG ─────────────────────────────────────────────────────
const INDIA_STATES = [
  {
    id: "rajasthan", name: "Rajasthan",
    tagline: "Desert Kingdoms & Palace Grandeur",
    packages: [
      { id: "raj-palace", title: "Heritage Palace Stay — Jaipur & Amber Fort", specs: "4 Days / 3 Nights | Royal Heritage Suite | Fort Walk & Sound & Light Show", price: "₹24,500", style: "Honeymoon", img: "https://images.pexels.com/photos/17070404/pexels-photo-17070404.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940", testid: "enquire-rajasthan-palace" },
      { id: "raj-desert", title: "Luxury Desert Glamping — Jaisalmer Dunes", specs: "3 Days / 2 Nights | Swiss Luxury Tent | Camel Safari & Bonfire Stargazing", price: "₹18,900", style: "Group Expeditions", img: "https://images.pexels.com/photos/29368152/pexels-photo-29368152.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940", testid: "enquire-rajasthan-desert" },
      { id: "raj-lake", title: "Udaipur Lake Palace Romantic Escape", specs: "4 Days / 3 Nights | Lake View Palace Suite | Shikara Sunset Cruise", price: "₹32,000", style: "Honeymoon", img: "https://images.unsplash.com/photo-1599661046289-e31897846e41?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85", testid: "enquire-rajasthan-lake" }
    ]
  },
  {
    id: "kerala", name: "Kerala",
    tagline: "Backwaters, Cliffs & Rainforest Canopies",
    packages: [
      { id: "kerala-hw", title: "Romantic Kerala Backwaters & Munnar Hills", specs: "5 Days / 4 Nights | Heritage Houseboat Stay | Spice Plantation Tour", price: "₹16,900", style: "Honeymoon", img: "https://images.pexels.com/photos/12950219/pexels-photo-12950219.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940", testid: "enquire-kerala-honeymoon" },
      { id: "kerala-varkala", title: "Varkala Cliff & Backwater Surf Nomad", specs: "4 Days / 3 Nights | Cliff-Top Surf Stay | Cafe Crawls & Ayurvedic Spa", price: "₹11,200", style: "Solo", img: "https://images.pexels.com/photos/12157092/pexels-photo-12157092.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940", testid: "enquire-varkala-solo" },
      { id: "kerala-wayanad", title: "Wayanad Rainforest & Wildlife Caravan", specs: "5 Days / 4 Nights | Treehouse Stay | Offroad Jeep Safari & Night Trail", price: "₹14,500", style: "Group Expeditions", img: "https://images.unsplash.com/photo-1699563257711-c7b96afd4ac0?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85", testid: "enquire-wayanad-group" }
    ]
  },
  {
    id: "goa", name: "Goa",
    tagline: "Sun-Bleached Beaches & Portuguese Splendour",
    packages: [
      { id: "goa-villa", title: "North Goa Boutique Beach Villa Escape", specs: "4 Days / 3 Nights | Private Beach Villa | Shack Dining & Sunset Cruise", price: "₹15,500", style: "Honeymoon", img: "https://images.pexels.com/photos/863985/pexels-photo-863985.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940", testid: "enquire-goa-villa" },
      { id: "goa-heritage", title: "Old Goa Heritage Walk & Spice Farm Retreat", specs: "3 Days / 2 Nights | Heritage Portuguese Villa | Spice Plantation Cooking Class", price: "₹12,200", style: "Solo", img: "https://images.pexels.com/photos/31897042/pexels-photo-31897042.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940", testid: "enquire-goa-heritage" },
      { id: "goa-group", title: "South Goa Quiet Beaches & Surf Group Camp", specs: "4 Days / 3 Nights | Beach Huts | Surf Lessons & Midnight Fire Circles", price: "₹9,800", style: "Group Expeditions", img: "https://images.unsplash.com/photo-1652820330085-82a0c2b88d78?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85", testid: "enquire-goa-group" }
    ]
  },
  {
    id: "himachal", name: "Himachal Pradesh",
    tagline: "Alpine Snow, Ski Slopes & Monastery Circuits",
    packages: [
      { id: "manali-ski", title: "Manali Luxury Ski & Snow Adventure", specs: "5 Days / 4 Nights | Mountain Chalet | Ski Lessons, Solang Valley & Hot Springs", price: "₹21,000", style: "Group Expeditions", img: "https://images.unsplash.com/photo-1679407985718-4b057b879652?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85", testid: "enquire-manali-ski" },
      { id: "dharamshala", title: "Dharamshala Monastery Circuit & Himalayan Trek", specs: "4 Days / 3 Nights | Monastery Homestay | Guided Trek & Tibetan Cuisine", price: "₹13,500", style: "Solo", img: "https://images.pexels.com/photos/5110976/pexels-photo-5110976.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940", testid: "enquire-dharamshala" },
      { id: "spiti", title: "Spiti Valley Cold Desert Expedition", specs: "8 Days / 7 Nights | Remote Village Stays | High-Altitude Jeep Trail", price: "₹24,800", style: "Group Expeditions", img: "https://images.pexels.com/photos/37466126/pexels-photo-37466126.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940", testid: "enquire-spiti" }
    ]
  },
  {
    id: "kashmir", name: "Jammu & Kashmir",
    tagline: "Paradise Houseboats, Meadow Walks & Ski Peaks",
    packages: [
      { id: "dal-lake", title: "Dal Lake Luxury Houseboat Escape — Srinagar", specs: "4 Days / 3 Nights | Premium Houseboat | Shikara Sunrise Ride & Floating Market", price: "₹19,500", style: "Honeymoon", img: "https://images.pexels.com/photos/33466692/pexels-photo-33466692.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940", testid: "enquire-dal-lake" },
      { id: "gulmarg", title: "Gulmarg Ski Expedition & Gondola Adventure", specs: "3 Days / 2 Nights | Ski Resort Lodge | Cable Car Rides & Slope Lessons", price: "₹22,000", style: "Group Expeditions", img: "https://images.pexels.com/photos/33054187/pexels-photo-33054187.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940", testid: "enquire-gulmarg" },
      { id: "pahalgam", title: "Pahalgam Meadow & Betaab Valley Trek", specs: "3 Days / 2 Nights | Log Cabin Stay | Valley Walks & Horse Riding", price: "₹14,200", style: "Solo", img: "https://images.unsplash.com/photo-1598091383021-15ddea10925d?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85", testid: "enquire-pahalgam" }
    ]
  },
  {
    id: "uttarakhand", name: "Uttarakhand",
    tagline: "Sacred Rivers, Yoga Retreats & Jungle Safaris",
    packages: [
      { id: "rishikesh", title: "Rishikesh Luxury Yoga & Wellness Retreat", specs: "5 Days / 4 Nights | Riverside Ashram Suite | Yoga, Ayurveda & White-Water Rafting", price: "₹17,200", style: "Solo", img: "https://images.unsplash.com/photo-1650341278999-d1b5142cfe30?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85", testid: "enquire-rishikesh" },
      { id: "corbett", title: "Jim Corbett Jungle Safari & Tiger Lodge", specs: "3 Days / 2 Nights | Forest Lodge Cottage | 2 Jeep Safaris & Bird-Watching", price: "₹16,800", style: "Group Expeditions", img: "https://images.unsplash.com/photo-1650341259809-9314b0de9268?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85", testid: "enquire-corbett" },
      { id: "char-dham", title: "Char Dham Sacred Pilgrimage Circuit", specs: "10 Days / 9 Nights | Deluxe Guest Houses | Expert Religious Guide", price: "₹28,500", style: "Group Expeditions", img: "https://images.unsplash.com/photo-1649147313351-c86537fda0eb?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85", testid: "enquire-char-dham" }
    ]
  },
  {
    id: "karnataka", name: "Karnataka",
    tagline: "Ancient Ruins, Coffee Estates & Wild Forests",
    packages: [
      { id: "hampi", title: "The Ruins of Hampi & Hippie Island Explorer", specs: "4 Days / 3 Nights | Riverside Homestay | Bouldering & Cycling Guides", price: "₹9,500", style: "Solo", img: "https://images.pexels.com/photos/37626186/pexels-photo-37626186.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940", testid: "enquire-hampi" },
      { id: "coorg", title: "Coorg Coffee Estate Luxury Escape", specs: "4 Days / 3 Nights | Private Pool Villa | Plantation Walks & Waterfalls", price: "₹19,800", style: "Honeymoon", img: "https://images.pexels.com/photos/35688870/pexels-photo-35688870.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940", testid: "enquire-coorg" },
      { id: "kabini", title: "Kabini Wildlife Reserve Jungle Safari", specs: "3 Days / 2 Nights | Eco-Lodge | Coracle Boat Ride & Elephant Sightings", price: "₹15,200", style: "Group Expeditions", img: "https://images.unsplash.com/photo-1634874587938-08ca3a5f1a48?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85", testid: "enquire-kabini" }
    ]
  },
  {
    id: "maharashtra", name: "Maharashtra",
    tagline: "UNESCO Caves, Lakeside Retreats & Monsoon Forts",
    packages: [
      { id: "ajanta", title: "Ajanta & Ellora UNESCO Cave Trek", specs: "4 Days / 3 Nights | Aurangabad Heritage Hotel | Expert Archaeological Guide", price: "₹13,500", style: "Solo", img: "https://images.pexels.com/photos/37602278/pexels-photo-37602278.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940", testid: "enquire-ajanta" },
      { id: "lavasa", title: "Lavasa Lakeside Hill Station Resort", specs: "3 Days / 2 Nights | Italian-Theme Lakeside Villa | Boating & Waterfall Hike", price: "₹11,900", style: "Honeymoon", img: "https://images.unsplash.com/photo-1631774934803-554afa7371c9?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85", testid: "enquire-lavasa" },
      { id: "lonavala", title: "Lonavala Monsoon Escape & Lohagad Fort Hike", specs: "2 Days / 1 Night | Heritage Bungalow | Bhaja Caves & Lohagad Fort Trek", price: "₹7,800", style: "Group Expeditions", img: "https://images.pexels.com/photos/4882665/pexels-photo-4882665.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940", testid: "enquire-lonavala" }
    ]
  },
  {
    id: "tamilnadu", name: "Tamil Nadu",
    tagline: "Dravidian Temples, Heritage Mansions & Hill Escapes",
    packages: [
      { id: "chettinad", title: "Chettinad Palace Cuisine & Heritage Trail", specs: "4 Days / 3 Nights | Chettinad Mansion | Private Cooking Sessions & Antique Bazaars", price: "₹14,900", style: "Solo", img: "https://images.pexels.com/photos/23914402/pexels-photo-23914402.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940", testid: "enquire-chettinad" },
      { id: "kodaikanal", title: "Kodaikanal Hill Station Romantic Escape", specs: "4 Days / 3 Nights | Valley View Cottage | Coaker's Walk & Boating", price: "₹12,500", style: "Honeymoon", img: "https://images.pexels.com/photos/5690494/pexels-photo-5690494.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940", testid: "enquire-kodaikanal" },
      { id: "meenakshi", title: "Meenakshi Temple Circuit & Madurai Heritage", specs: "3 Days / 2 Nights | Heritage Boutique Hotel | Temple Night Ceremony & Silk Trail", price: "₹9,900", style: "Group Expeditions", img: "https://images.unsplash.com/photo-1759509334486-3ae81c4dcb01?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85", testid: "enquire-meenakshi" }
    ]
  },
  {
    id: "sikkim", name: "Sikkim",
    tagline: "Alpine Treks, Monasteries & Hidden Himalayan Lakes",
    packages: [
      { id: "dzongri", title: "Dzongri Alpine Trek & Kanchenjunga Views", specs: "7 Days / 6 Nights | Mountain Trekker Camps | High-Altitude Himalayan Experience", price: "₹19,200", style: "Solo", img: "https://images.pexels.com/photos/36889954/pexels-photo-36889954.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940", testid: "enquire-dzongri" },
      { id: "gurudongmar", title: "Gurudongmar Sacred Lake Expedition", specs: "5 Days / 4 Nights | North Sikkim Eco-Stays | Highest Sacred Lake in Sikkim", price: "₹16,800", style: "Group Expeditions", img: "https://images.unsplash.com/photo-1648963798678-a921079b98b9?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85", testid: "enquire-gurudongmar" },
      { id: "rumtek", title: "Rumtek Monastery & Buddhist Spiritual Retreat", specs: "4 Days / 3 Nights | Monastery-Adjacent Lodge | Meditation Sessions & Buddhist Rituals", price: "₹13,500", style: "Solo", img: "https://images.pexels.com/photos/37866434/pexels-photo-37866434.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940", testid: "enquire-rumtek" }
    ]
  }
];

// ─── INTERNATIONAL CONTINENTS CATALOG ─────────────────────────────────────────
const INTL_CONTINENTS = [
  {
    id: "europe", name: "Europe",
    tagline: "Cliffside Villages, Alpine Peaks & Northern Lights",
    packages: [
      { id: "santorini", title: "Greek Island Hopper: Santorini & Mykonos", specs: "7 Days / 6 Nights | Clifftop Caldera Villa | Yacht Day Cruise & Sunset Wine Tasting", price: "₹89,500", style: "Honeymoon", img: "https://images.unsplash.com/photo-1724075700355-e54bf35dc5be?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85", testid: "enquire-santorini" },
      { id: "swiss-alps", title: "Swiss Alps Winter Escape — Geneva & Zermatt", specs: "7 Days / 6 Nights | Alpine Chalet Suite | Ski Slopes, Glacier Train & Matterhorn Views", price: "₹1,12,000", style: "Group Expeditions", img: "https://images.unsplash.com/photo-1629470035936-3296c3bd8237?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85", testid: "enquire-swiss-alps" },
      { id: "iceland", title: "Iceland Northern Lights & Glacier Walk", specs: "6 Days / 5 Nights | Glass-Dome Aurora Cabin | Blue Lagoon, Geysers & Black Sand Beaches", price: "₹98,000", style: "Honeymoon", img: "https://images.pexels.com/photos/10173043/pexels-photo-10173043.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940", testid: "enquire-iceland" }
    ]
  },
  {
    id: "asia", name: "Asia",
    tagline: "Ancient Dynasties, Avatar Mountains & Zen Temples",
    packages: [
      { id: "zhangjiajie", title: "Zhangjiajie Avatar Mountains & Forbidden City — China", specs: "8 Days / 7 Nights | Cliff Hotel | Glass Bridge, Cable Car & Beijing Imperial Tour", price: "₹78,500", style: "Group Expeditions", img: "https://images.unsplash.com/photo-1567266565446-d9c40ccf59a4?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85", testid: "enquire-zhangjiajie" },
      { id: "kyoto", title: "Kyoto & Osaka Luxury Japan Circuit", specs: "8 Days / 7 Nights | Traditional Ryokan | Cherry Blossom Gardens & Street Food Trail", price: "₹95,000", style: "Honeymoon", img: "https://images.pexels.com/photos/31855372/pexels-photo-31855372.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940", testid: "enquire-kyoto" },
      { id: "bali-intl", title: "The Slow-Paced Bali & Nusa Penida Escape", specs: "6 Days / 5 Nights | Ubud Private Villa | Private Chauffeur & Temple Ceremonies", price: "₹21,500", style: "Honeymoon", img: "https://images.pexels.com/photos/1724424/pexels-photo-1724424.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940", testid: "enquire-bali-intl" }
    ]
  },
  {
    id: "americas", name: "The Americas",
    tagline: "Inca Citadels, Patagonian Glaciers & Pacific Coastlines",
    packages: [
      { id: "machu-picchu", title: "Machu Picchu & Sacred Valley Inca Trail", specs: "7 Days / 6 Nights | Boutique Cusco Hotel | Sunrise at Machu Picchu & Heritage Train", price: "₹87,000", style: "Group Expeditions", img: "https://images.unsplash.com/photo-1609944433409-81bda5323abc?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85", testid: "enquire-machu-picchu" },
      { id: "patagonia", title: "Patagonia Glaciers & End of the World Trek", specs: "10 Days / 9 Nights | Eco-Lodge | Perito Moreno Glacier Walk & Torres del Paine", price: "₹1,45,000", style: "Solo", img: "https://images.pexels.com/photos/13923040/pexels-photo-13923040.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940", testid: "enquire-patagonia" },
      { id: "new-england", title: "New York City & New England Fall Foliage Road Trip", specs: "8 Days / 7 Nights | Manhattan Boutique Hotel | NYC Highlights & Fall Foliage Drive", price: "₹1,18,000", style: "Group Expeditions", img: "https://images.pexels.com/photos/27823612/pexels-photo-27823612.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940", testid: "enquire-new-england" }
    ]
  },
  {
    id: "africa-me", name: "Africa & Middle East",
    tagline: "Big Five Safaris, Sahara Dunes & Ancient Trade Routes",
    packages: [
      { id: "serengeti", title: "Serengeti Great Migration Safari — Tanzania", specs: "7 Days / 6 Nights | Luxury Tented Camp | Open-Air Jeep Safari & Wildebeest Migration", price: "₹1,25,000", style: "Group Expeditions", img: "https://images.unsplash.com/photo-1564760055775-d63b17a55c44?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85", testid: "enquire-serengeti" },
      { id: "marrakech", title: "Marrakech Medina & Sahara Desert Expedition", specs: "6 Days / 5 Nights | Riad Palace Stay | Sahara Camel Trek & Atlas Mountain Drive", price: "₹67,500", style: "Solo", img: "https://images.pexels.com/photos/6621125/pexels-photo-6621125.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940", testid: "enquire-marrakech" },
      { id: "dubai", title: "The Epic Dubai City & Desert Safari Expedition", specs: "5 Days / 4 Nights | 5-Star Downtown Hotel | Burj Khalifa, Desert Dunes & Luxury Dhow", price: "₹35,500", style: "Group Expeditions", img: "https://images.pexels.com/photos/28639031/pexels-photo-28639031.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940", testid: "enquire-dubai" }
    ]
  },
  {
    id: "oceania", name: "Oceania & Antarctica",
    tagline: "Great Barrier Reef, Fjordland & Polar Expeditions",
    packages: [
      { id: "sydney-reef", title: "Sydney Opera House & Great Barrier Reef", specs: "8 Days / 7 Nights | Harbour-View Hotel | Reef Dive & Sydney Harbour Bridge Climb", price: "₹1,08,000", style: "Group Expeditions", img: "https://images.unsplash.com/photo-1549180030-48bf079fb38a?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85", testid: "enquire-sydney-reef" },
      { id: "nz-fjords", title: "New Zealand Fjordland — Milford Sound & Queenstown", specs: "9 Days / 8 Nights | Lakeside Lodge | Milford Sound Cruise & Glacier Hike", price: "₹1,22,000", style: "Solo", img: "https://images.pexels.com/photos/237260/pexels-photo-237260.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940", testid: "enquire-nz-fjords" },
      { id: "antarctica", title: "Antarctic Expedition — Ultima Thule Experience", specs: "14 Days / 13 Nights | Polar Research Vessel | Ice Landings, Penguin Colonies & Aurora Australis", price: "₹4,50,000", style: "Solo", img: "https://images.pexels.com/photos/797727/pexels-photo-797727.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940", testid: "enquire-antarctica" }
    ]
  }
];

// ─── HERO PHRASES (Superscript Typewriter) ────────────────────────────────────
const HERO_PHRASES = [
  { prefix: "WITH", main: "FRIENDS" },
  { prefix: "WITH", main: "FAMILY" },
  { prefix: "WITH YOUR", main: "PARTNER" },
  { prefix: "WITH YOUR", main: "TEAM" },
  { prefix: "FOR", main: "YOURSELF" },
];

// ─── Superscript Typewriter Hook ──────────────────────────────────────────────
function useSuperscriptTypewriter(phrases, typeSpeed = 85, deleteSpeed = 50, pauseMs = 2000) {
  const [state, setState] = useState({ typedMain: "", phraseIdx: 0, deleting: false, paused: false });
  useEffect(() => {
    const { typedMain, phraseIdx, deleting, paused } = state;
    const main = phrases[phraseIdx].main;
    if (paused) {
      const t = setTimeout(() => setState(s => ({ ...s, deleting: true, paused: false })), pauseMs);
      return () => clearTimeout(t);
    }
    if (!deleting && typedMain === main) { setState(s => ({ ...s, paused: true })); return; }
    const delay = deleting ? deleteSpeed : typeSpeed;
    const t = setTimeout(() => {
      if (!deleting) setState(s => ({ ...s, typedMain: main.slice(0, s.typedMain.length + 1) }));
      else if (typedMain.length > 0) setState(s => ({ ...s, typedMain: s.typedMain.slice(0, -1) }));
      else setState(s => ({ ...s, deleting: false, phraseIdx: (s.phraseIdx + 1) % phrases.length }));
    }, delay);
    return () => clearTimeout(t);
  }, [state, phrases, typeSpeed, deleteSpeed, pauseMs]);
  return { prefix: phrases[state.phraseIdx].prefix, typedMain: state.typedMain };
}

// ─── Scroll Reveal Hook ────────────────────────────────────────────────────────
function useScrollReveal(threshold = 0.08) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) { setVisible(true); obs.unobserve(e.target); } }, { threshold });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [threshold]);
  return [ref, visible];
}

function ScrollTo(id) { document.getElementById(id)?.scrollIntoView({ behavior: "smooth" }); }

// ─── Package Card (overlay design) ────────────────────────────────────────────
function PackageCard({ card, onEnquire }) {
  const [ref, vis] = useScrollReveal(0.05);
  return (
    <div ref={ref} className={`group relative overflow-hidden transition-all duration-700 ${vis ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
      <div className="relative overflow-hidden" style={{ aspectRatio: "3/4" }}>
        <img src={card.img} alt={card.title} loading="lazy"
          className="w-full h-full object-cover group-hover:scale-105"
          style={{ transition: "transform 0.4s ease" }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/96 via-black/55 to-transparent pointer-events-none" />
        <div className="absolute bottom-0 left-0 right-0 p-4 z-10">
          <span className="text-[9px] font-extrabold uppercase tracking-widest px-2 py-0.5 bg-[#FCD116] text-[#0B0C10] mb-2 inline-block">{card.style}</span>
          <h3 className="text-sm font-bold text-white mb-1 leading-snug line-clamp-2">{card.title}</h3>
          <p className="text-[10px] text-gray-400 mb-3 leading-relaxed line-clamp-2">{card.specs}</p>
          <div className="flex items-end justify-between gap-2">
            <div>
              <span className="text-[9px] text-gray-500 block">Starting from</span>
              <span className="text-[#FCD116] text-base font-extrabold leading-none">{card.price}</span>
              <span className="text-[9px] text-gray-500 block mt-0.5">/ person (Excl. Flights)</span>
            </div>
            <button onClick={() => onEnquire(card.title)}
              className="bg-[#FCD116] text-[#0B0C10] font-bold text-[10px] px-3 py-2 uppercase tracking-wider hover:bg-yellow-300 transition-colors shrink-0"
              data-testid={card.testid}
            >
              Enquire Now
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Carousel Card ────────────────────────────────────────────────────────────
function CarouselCard({ card, onEnquire }) {
  return (
    <div
      className="carousel-card group relative shrink-0 overflow-hidden cursor-pointer"
      style={{ width: "clamp(240px, 22vw, 296px)" }}
      onClick={() => onEnquire(card.title)}
      data-testid={`carousel-card-${card.id}`}
    >
      <div
        className="relative overflow-hidden transition-all duration-300 group-hover:-translate-y-1.5 group-hover:shadow-[0_16px_48px_rgba(0,0,0,0.7)]"
        style={{ aspectRatio: "3/4" }}
      >
        <img
          src={card.img}
          alt={card.title}
          loading="lazy"
          draggable="false"
          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/20 to-transparent" />
        <div className="absolute bottom-0 left-0 right-0 h-[2px] bg-[#FCD116] scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left" />
        <div className="absolute top-3 left-3 z-10">
          <span className="text-[8px] font-extrabold uppercase tracking-widest px-2 py-0.5 bg-[#FCD116] text-[#0B0C10]">
            {card.collectionType === "Across India" ? "India" : "Intl"}
          </span>
        </div>
        <div className="absolute bottom-0 left-0 right-0 p-4 z-10">
          <p className="text-[9px] text-[#FCD116] font-bold uppercase tracking-widest mb-1.5">{card.collectionLabel}</p>
          <h3 className="text-[13px] font-bold text-white leading-snug line-clamp-2 mb-1">{card.title}</h3>
          <p className="text-[10px] text-gray-400">{card.specs.split("|")[0].trim()}</p>
        </div>
      </div>
    </div>
  );
}

// ─── Handpicked Experiences Carousel ──────────────────────────────────────────
function HandpickedCarousel({ onEnquire }) {
  const scrollRef = useRef(null);
  const [isHovered, setIsHovered] = useState(false);
  const isDragging = useRef(false);
  const dragStart = useRef({ x: 0, scrollL: 0 });
  const autoTimer = useRef(null);
  const isResetting = useRef(false);
  const [sectionRef, sectionVis] = useScrollReveal(0.05);

  const allPackages = useMemo(() => {
    const pkgs = [];
    INDIA_STATES.forEach(s => s.packages.forEach(p =>
      pkgs.push({ ...p, collectionLabel: s.name, collectionType: "Across India" })
    ));
    // International packages added twice for 2x weighting in carousel
    INTL_CONTINENTS.forEach(c => c.packages.forEach(p => {
      pkgs.push({ ...p, collectionLabel: c.name, collectionType: "International Collections" });
      pkgs.push({ ...p, id: p.id + "-b", collectionLabel: c.name, collectionType: "International Collections" });
    }));
    for (let i = pkgs.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [pkgs[i], pkgs[j]] = [pkgs[j], pkgs[i]];
    }
    return pkgs;
  }, []);

  const tripled = useMemo(() => [...allPackages, ...allPackages, ...allPackages], [allPackages]);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el || allPackages.length === 0) return;
    const t = setTimeout(() => { el.scrollLeft = el.scrollWidth / 3; }, 250);
    return () => clearTimeout(t);
  }, [allPackages.length]);

  const onScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el || isResetting.current) return;
    const third = el.scrollWidth / 3;
    if (el.scrollLeft >= third * 1.95) {
      isResetting.current = true;
      el.scrollLeft = third + (el.scrollLeft - third * 1.95);
      requestAnimationFrame(() => { isResetting.current = false; });
    } else if (el.scrollLeft <= third * 0.05) {
      isResetting.current = true;
      el.scrollLeft = third - (third * 0.05 - el.scrollLeft);
      requestAnimationFrame(() => { isResetting.current = false; });
    }
  }, []);

  const stopAuto = useCallback(() => {
    if (autoTimer.current) { clearInterval(autoTimer.current); autoTimer.current = null; }
  }, []);

  const startAuto = useCallback(() => {
    stopAuto();
    autoTimer.current = setInterval(() => {
      const el = scrollRef.current;
      const card = el?.querySelector('.carousel-card');
      if (!el || !card) return;
      el.scrollBy({ left: card.offsetWidth + 16, behavior: 'smooth' });
    }, 5000);
  }, [stopAuto]);

  useEffect(() => {
    if (!isHovered) startAuto(); else stopAuto();
    return stopAuto;
  }, [isHovered, startAuto, stopAuto]);

  const navigateCarousel = (dir) => {
    const el = scrollRef.current;
    const card = el?.querySelector('.carousel-card');
    if (!el || !card) return;
    el.scrollBy({ left: dir * (card.offsetWidth + 16), behavior: 'smooth' });
  };

  const onMD = (e) => {
    if (e.button !== 0) return;
    isDragging.current = true;
    dragStart.current = { x: e.pageX, scrollL: scrollRef.current.scrollLeft };
    if (scrollRef.current) scrollRef.current.style.cursor = 'grabbing';
  };
  const onMM = (e) => {
    if (!isDragging.current) return;
    e.preventDefault();
    scrollRef.current.scrollLeft = dragStart.current.scrollL - (e.pageX - dragStart.current.x) * 1.5;
  };
  const onMU = () => {
    isDragging.current = false;
    if (scrollRef.current) scrollRef.current.style.cursor = 'grab';
  };

  return (
    <section
      id="handpicked"
      ref={sectionRef}
      className={`py-20 transition-all duration-1000 ${sectionVis ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}
      style={{ background: "#0B0C10", borderBottom: "1px solid rgba(255,255,255,0.05)" }}
    >
      <div className="max-w-7xl mx-auto px-6 mb-10">
        <span className="text-[10px] uppercase tracking-[0.3em] text-[#FCD116] font-bold mb-3 block border-l-4 border-[#FCD116] pl-3">
          Handpicked Experiences
        </span>
        <div className="flex items-end justify-between gap-4">
          <div>
            <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tight mb-2">
              Curated Packages From Our Collections
            </h2>
            <p className="text-sm text-gray-400 font-light max-w-lg leading-relaxed">
              Discover destinations selected from Across India and International Collections.
            </p>
          </div>
          <div className="hidden sm:flex items-center gap-3 shrink-0">
            <button
              onClick={() => navigateCarousel(-1)}
              className="h-10 w-10 rounded-full flex items-center justify-center border border-white/15 hover:border-[#FCD116]/60 hover:bg-[#FCD116]/8 transition-all duration-300 hover:scale-110"
              style={{ background: "rgba(11,12,16,0.8)" }}
              data-testid="carousel-prev-btn"
            >
              <ChevronLeft className="h-5 w-5 text-white" />
            </button>
            <button
              onClick={() => navigateCarousel(1)}
              className="h-10 w-10 rounded-full flex items-center justify-center border border-white/15 hover:border-[#FCD116]/60 hover:bg-[#FCD116]/8 transition-all duration-300 hover:scale-110"
              style={{ background: "rgba(11,12,16,0.8)" }}
              data-testid="carousel-next-btn"
            >
              <ChevronRight className="h-5 w-5 text-white" />
            </button>
          </div>
        </div>
      </div>

      <div
        className="relative"
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => { setIsHovered(false); onMU(); }}
      >
        <div
          ref={scrollRef}
          className="flex gap-4 overflow-x-auto pb-4 px-6 carousel-scroll"
          style={{ cursor: 'grab', WebkitOverflowScrolling: 'touch' }}
          onScroll={onScroll}
          onMouseDown={onMD}
          onMouseMove={onMM}
          onMouseUp={onMU}
          data-testid="carousel-container"
        >
          {tripled.map((pkg, idx) => (
            <CarouselCard key={`${pkg.id}-${idx}`} card={pkg} onEnquire={onEnquire} />
          ))}
        </div>
      </div>
    </section>
  );
}

// ─── Main Landing Page ─────────────────────────────────────────────────────────
function ViviLandingPage() {
  const navigate = useNavigate();

  // Navbar scroll shrink
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const handler = () => setScrolled(window.scrollY > 50);
    window.addEventListener("scroll", handler, { passive: true });
    return () => window.removeEventListener("scroll", handler);
  }, []);

  // Collapsible state
  const [openFolder, setOpenFolder] = useState(null);          // 'india' | 'international'
  const [openState, setOpenState] = useState(null);            // state.id
  const [openContinent, setOpenContinent] = useState(null);    // continent.id

  // Modal
  const [modalOpen, setModalOpen] = useState(false);
  const [formData, setFormData] = useState({ fullName: "", whatsapp: "", destination: "", travelers: "Couple", targetMonth: "January 2026" });
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Scroll reveals
  const [catalogRef, catalogVis] = useScrollReveal(0.04);
  const [philRef, philVis] = useScrollReveal(0.08);
  const [diaryRef, diaryVis] = useScrollReveal(0.08);
  const [contactRef, contactVis] = useScrollReveal(0.08);

  // Superscript Typewriter
  const { prefix: heroPrefix, typedMain: heroTypedMain } = useSuperscriptTypewriter(HERO_PHRASES);

  // ─── Folder / Sub-section logic ───────────────────────────────────────────
  const toggleFolder = (folder) => {
    if (openFolder === folder) {
      setOpenFolder(null); setOpenState(null); setOpenContinent(null);
    } else {
      setOpenFolder(folder);
      if (folder === "india") { setOpenState(INDIA_STATES[0].id); setOpenContinent(null); }
      else { setOpenContinent(INTL_CONTINENTS[0].id); setOpenState(null); }
    }
  };
  const selectState = (id) => setOpenState(openState === id ? null : id);
  const selectContinent = (id) => setOpenContinent(openContinent === id ? null : id);

  // ─── Enquiry helpers ──────────────────────────────────────────────────────
  const openModal = (dest = "") => {
    setFormData(prev => ({ ...prev, destination: dest, fullName: "", whatsapp: "" }));
    setModalOpen(true);
  };
  const handleFormChange = (e) => setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.fullName.trim()) { toast.error("Please enter your full name"); return; }
    if (!formData.whatsapp.trim()) { toast.error("Please enter your WhatsApp number"); return; }
    setIsSubmitting(true);
    try {
      await axios.post(`${API}/enquiries`, {
        full_name: formData.fullName, whatsapp_number: formData.whatsapp,
        destination: formData.destination || "Custom Tailored Experience",
        travelers: formData.travelers, target_month: formData.targetMonth
      });
      toast.success("Request received! Our team will reach out within 2 hours.");
      setModalOpen(false);
      setFormData({ fullName: "", whatsapp: "", destination: "", travelers: "Couple", targetMonth: "January 2026" });
    } catch { toast.error("Something went wrong. Please try again."); }
    finally { setIsSubmitting(false); }
  };

  const activeStateData = INDIA_STATES.find(s => s.id === openState);
  const activeContinentData = INTL_CONTINENTS.find(c => c.id === openContinent);

  return (
    <div style={{ background: "#0B0C10" }} className="text-white min-h-screen selection:bg-[#FCD116] selection:text-black">

      {/* ══════════════════════════════════════════════════════════════
          NAVIGATION
      ══════════════════════════════════════════════════════════════ */}
      <header
        className="fixed top-0 left-0 right-0 z-50 transition-all duration-400"
        style={{
          backdropFilter: "blur(10px)",
          WebkitBackdropFilter: "blur(10px)",
          background: scrolled ? "rgba(11,12,16,0.95)" : "rgba(11,12,16,0.80)",
          borderBottom: scrolled ? "1px solid rgba(255,255,255,0.08)" : "1px solid transparent"
        }}>
        <div className={`max-w-7xl mx-auto px-6 flex items-center justify-between transition-all duration-400 ${scrolled ? "h-[60px]" : "h-[76px]"}`}>
          <div className="cursor-pointer" onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}>
            <img src="https://customer-assets.emergentagent.com/job_ed8a34a7-db3c-4095-a429-13889e15a3bb/artifacts/hqhd52hw_VIVI.png"
              alt="VIVI HOLIDAYS" style={{ height: "65px", padding: "6px 0", width: "auto", objectFit: "contain" }}
              data-testid="vivi-logo" />
          </div>
          <nav className="hidden md:flex items-center space-x-8">
            {[["Explore Collections", "catalog"], ["Custom Itineraries", null], ["Our Story", "philosophy"], ["Guest Diaries", "guest-diaries"]].map(([label, id]) => (
              <button key={label}
                onClick={() => id ? ScrollTo(id) : openModal("Custom Itinerary")}
                className="font-medium text-sm text-gray-300 hover:text-[#FCD116] transition-colors relative group"
                data-testid={`nav-${label.toLowerCase().replace(/\s+/g, "-")}`}
              >
                {label}
                <span className="absolute -bottom-1 left-0 w-0 h-[2px] bg-[#FCD116] transition-all duration-300 group-hover:w-full" />
              </button>
            ))}
          </nav>
          <button onClick={() => openModal("")}
            className="bg-[#FCD116] text-[#0B0C10] font-bold text-sm tracking-wide py-3 px-7 transition-all duration-300 hover:-translate-y-[2px] hover:bg-yellow-300 hover:shadow-[0_6px_20px_rgba(252,209,22,0.35)] active:translate-y-0"
            data-testid="header-enquire-now-button">
            Enquire Now
          </button>
        </div>
      </header>

      {/* ══════════════════════════════════════════════════════════════
          HERO — GRAYSCALE SCENIC BACKGROUND + LAYERED TYPEWRITER
      ══════════════════════════════════════════════════════════════ */}
      <section className="relative min-h-screen overflow-hidden" style={{ background: "#000000" }}>

        {/* LAYER 0: Full-bleed cinematic scenic mountain background — full natural color */}
        <div className="absolute inset-0 z-0 pointer-events-none">
          <img
            src="https://images.pexels.com/photos/17044220/pexels-photo-17044220.jpeg?auto=compress&cs=tinysrgb&dpr=2&w=1920"
            alt=""
            aria-hidden="true"
            className="w-full h-full object-cover object-center"
          />
          {/* Minimal overlay — max 12% for text readability only */}
          <div className="absolute inset-0" style={{ background: "rgba(0,0,0,0.12)" }} />
          {/* Top gradient to keep nav readable */}
          <div className="absolute top-0 left-0 right-0" style={{ height: "220px", background: "linear-gradient(to bottom, rgba(0,0,0,0.55) 0%, transparent 100%)" }} />
          {/* Bottom gradient for button area */}
          <div className="absolute bottom-0 left-0 right-0" style={{ height: "200px", background: "linear-gradient(to top, rgba(0,0,0,0.60) 0%, transparent 100%)" }} />
        </div>

        {/* LAYER 1: Subtle brand-yellow atmospheric glow at base */}
        <div className="absolute inset-0 z-[2] pointer-events-none" style={{
          background: "radial-gradient(ellipse 70% 35% at 50% 100%, rgba(252,209,22,0.06) 0%, transparent 55%)"
        }} />

        {/* LAYER 2: Foreground — Headline · Typewriter · CTAs grouped in vertical center */}
        <div className="relative z-[20] flex flex-col items-center justify-center min-h-screen px-6">

          {/* TOP: Headline — no forced padding, justify-center handles vertical centering */}
          <div className="flex flex-col items-center text-center">
            <h1
              className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-extrabold tracking-tighter text-white leading-[1.05]"
              data-testid="hero-headline"
              style={{ textShadow: "0 2px 32px rgba(0,0,0,0.8)" }}
            >
              Explore the World
            </h1>
          </div>

          {/* MIDDLE: Superscript Typewriter */}
          <div className="w-full flex items-center justify-center overflow-hidden py-6 sm:py-8 pointer-events-none select-none" data-testid="hero-typewriter">
            <span style={{
              fontFamily: "'Outfit', sans-serif",
              fontWeight: 900,
              fontSize: "clamp(60px, 15vw, 200px)",
              color: "#FCD116",
              opacity: 0.80,
              letterSpacing: "0.06em",
              lineHeight: 1,
              userSelect: "none",
              textShadow: "0 0 80px rgba(252,209,22,0.18)"
            }}>
              {heroTypedMain.length > 0 ? (
                <>
                  <span style={{ position: "relative", display: "inline-block" }}>
                    <span style={{
                      position: "absolute",
                      bottom: "100%",
                      left: "0.05em",
                      fontSize: "clamp(9px, 1.3vw, 18px)",
                      fontWeight: 900,
                      letterSpacing: "0.22em",
                      color: "#FCD116",
                      opacity: 0.95,
                      lineHeight: 1,
                      whiteSpace: "nowrap",
                      paddingBottom: "0.12em",
                      fontFamily: "'Outfit', sans-serif",
                    }}>
                      {heroPrefix}
                    </span>
                    {heroTypedMain[0]}
                  </span>
                  {heroTypedMain.slice(1)}
                </>
              ) : null}
              <span className="typewriter-cursor" />
            </span>
          </div>

          {/* BOTTOM: Primary CTAs */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mt-6 sm:mt-8">
            <button
              onClick={() => { setOpenFolder("india"); setOpenState(INDIA_STATES[0].id); setTimeout(() => ScrollTo("catalog"), 200); }}
              className="flex items-center gap-2 border-2 border-white/60 text-white font-bold py-4 px-10 text-sm tracking-wide transition-all duration-300 hover:-translate-y-[2px] hover:border-white hover:bg-white/10 hover:shadow-[0_8px_24px_rgba(255,255,255,0.15)] active:translate-y-0"
              data-testid="hero-cta-india">
              <Mountain className="h-4 w-4" /> Explore India
            </button>
            <button
              onClick={() => { setOpenFolder("international"); setOpenContinent(INTL_CONTINENTS[0].id); setTimeout(() => ScrollTo("catalog"), 200); }}
              className="flex items-center gap-2 bg-[#FCD116] text-[#0B0C10] font-bold py-4 px-10 text-sm tracking-wide transition-all duration-300 hover:-translate-y-[2px] hover:bg-yellow-300 hover:shadow-[0_8px_24px_rgba(252,209,22,0.40)] active:translate-y-0"
              data-testid="hero-cta-intl">
              <Globe className="h-4 w-4" /> International Collections
            </button>
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════════
          HANDPICKED EXPERIENCES CAROUSEL
      ══════════════════════════════════════════════════════════════ */}
      <HandpickedCarousel onEnquire={openModal} />

      {/* ══════════════════════════════════════════════════════════════
          CATALOG — TWO-TIER COLLAPSIBLE ARCHITECTURE
      ══════════════════════════════════════════════════════════════ */}
      <section id="catalog" ref={catalogRef}
        className={`py-24 px-6 transition-all duration-1000 ${catalogVis ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
        <div className="max-w-7xl mx-auto">
          {/* Section header */}
          <div className="mb-14">
            <span className="text-[10px] uppercase tracking-[0.3em] text-[#FCD116] font-bold mb-3 block border-l-4 border-[#FCD116] pl-3">Curated Escapes</span>
            <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tight mb-3">Explore Collections</h2>
            <p className="text-sm text-gray-400 font-light max-w-xl leading-relaxed">
              Two master collections — Across India and International Collections. Click a folder below to reveal curated portfolios.
            </p>
          </div>

          {/* ── MASTER FOLDER BUTTONS ── */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            {/* ACROSS INDIA */}
            <button onClick={() => toggleFolder("india")}
              className={`group relative overflow-hidden flex items-center justify-between p-5 border transition-all duration-300 text-left ${openFolder === "india" ? "border-[#FCD116] bg-[#FCD116]/5" : "folder-pulse border-white/15 bg-[#13141A] hover:border-[#FCD116]/60"}`}
              data-testid="folder-across-india">
              {/* Horizontal collage background — India destinations */}
              <div className="absolute inset-0 flex pointer-events-none overflow-hidden">
                {[
                  "https://images.pexels.com/photos/17070404/pexels-photo-17070404.jpeg?auto=compress&cs=tinysrgb&dpr=1&h=200&w=250",
                  "https://images.pexels.com/photos/12950219/pexels-photo-12950219.jpeg?auto=compress&cs=tinysrgb&dpr=1&h=200&w=250",
                  "https://images.pexels.com/photos/5110976/pexels-photo-5110976.jpeg?auto=compress&cs=tinysrgb&dpr=1&h=200&w=250",
                  "https://images.pexels.com/photos/863985/pexels-photo-863985.jpeg?auto=compress&cs=tinysrgb&dpr=1&h=200&w=250"
                ].map((src, i) => (
                  <div key={i} className="flex-1 relative overflow-hidden">
                    <img src={src} alt="" loading="lazy" className="w-full h-full object-cover group-hover:scale-110"
                      style={{ opacity: openFolder === "india" ? 0.70 : 0.52, transition: "opacity 0.4s, transform 0.5s" }} />
                  </div>
                ))}
              </div>
              {/* Yellow left accent */}
              <div className={`absolute left-0 top-0 bottom-0 w-[3px] transition-all duration-300 ${openFolder === "india" ? "bg-[#FCD116]" : "bg-[#FCD116]/30 group-hover:bg-[#FCD116]"}`} />
              <div className="relative z-10">
                <h3 className="text-xl font-extrabold tracking-tight flex items-center gap-3"
                  style={{ textShadow: "0 0 14px rgba(0,0,0,1), 0 2px 8px rgba(0,0,0,1)" }}>
                  <Mountain className={`h-5 w-5 ${openFolder === "india" ? "text-[#FCD116]" : "text-gray-400 group-hover:text-[#FCD116]"} transition-colors duration-300`} />
                  Across India
                </h3>
              </div>
              <div className={`relative z-10 h-9 w-9 flex items-center justify-center border transition-all duration-300 ${openFolder === "india" ? "border-[#FCD116] bg-[#FCD116]/10" : "border-[#FCD116]/25 group-hover:border-[#FCD116] group-hover:bg-[#FCD116]/8"}`}>
                <ChevronRight className={`h-4 w-4 transition-all duration-300 ${openFolder === "india" ? "text-[#FCD116] rotate-90" : "text-[#FCD116]/60 group-hover:text-[#FCD116] group-hover:translate-x-0.5"}`} />
              </div>
            </button>

            {/* INTERNATIONAL COLLECTIONS */}
            <button onClick={() => toggleFolder("international")}
              className={`group relative overflow-hidden flex items-center justify-between p-5 border transition-all duration-300 text-left ${openFolder === "international" ? "border-[#FCD116] bg-[#FCD116]/5" : "folder-pulse border-white/15 bg-[#13141A] hover:border-[#FCD116]/60"}`}
              data-testid="folder-beyond-borders">
              {/* Horizontal collage background — International destinations */}
              <div className="absolute inset-0 flex pointer-events-none overflow-hidden">
                {[
                  "https://images.unsplash.com/photo-1724075700355-e54bf35dc5be?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=60&w=250",
                  "https://images.unsplash.com/photo-1629470035936-3296c3bd8237?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=60&w=250",
                  "https://images.pexels.com/photos/31855372/pexels-photo-31855372.jpeg?auto=compress&cs=tinysrgb&dpr=1&h=200&w=250",
                  "https://images.unsplash.com/photo-1564760055775-d63b17a55c44?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=60&w=250"
                ].map((src, i) => (
                  <div key={i} className="flex-1 relative overflow-hidden">
                    <img src={src} alt="" loading="lazy" className="w-full h-full object-cover group-hover:scale-110"
                      style={{ opacity: openFolder === "international" ? 0.70 : 0.52, transition: "opacity 0.4s, transform 0.5s" }} />
                  </div>
                ))}
              </div>
              {/* Yellow left accent */}
              <div className={`absolute left-0 top-0 bottom-0 w-[3px] transition-all duration-300 ${openFolder === "international" ? "bg-[#FCD116]" : "bg-[#FCD116]/30 group-hover:bg-[#FCD116]"}`} />
              <div className="relative z-10">
                <h3 className="text-xl font-extrabold tracking-tight flex items-center gap-3"
                  style={{ textShadow: "0 0 14px rgba(0,0,0,1), 0 2px 8px rgba(0,0,0,1)" }}>
                  <Globe className={`h-5 w-5 ${openFolder === "international" ? "text-[#FCD116]" : "text-gray-400 group-hover:text-[#FCD116]"} transition-colors duration-300`} />
                  International Collections
                </h3>
              </div>
              <div className={`relative z-10 h-9 w-9 flex items-center justify-center border transition-all duration-300 ${openFolder === "international" ? "border-[#FCD116] bg-[#FCD116]/10" : "border-[#FCD116]/25 group-hover:border-[#FCD116] group-hover:bg-[#FCD116]/8"}`}>
                <ChevronRight className={`h-4 w-4 transition-all duration-300 ${openFolder === "international" ? "text-[#FCD116] rotate-90" : "text-[#FCD116]/60 group-hover:text-[#FCD116] group-hover:translate-x-0.5"}`} />
              </div>
            </button>
          </div>

          {/* TAP TO EXPLORE nudge — visible when no folder is open */}
          {!openFolder && (
            <div className="flex flex-col items-center gap-2 py-5 select-none pointer-events-none" data-testid="tap-to-explore-nudge">
              <div className="flex items-center gap-3">
                <div className="h-px w-16 bg-[#FCD116]/20" />
                <span className="text-[9px] uppercase tracking-[0.35em] font-extrabold text-[#FCD116]/65">
                  Tap a folder to explore
                </span>
                <div className="h-px w-16 bg-[#FCD116]/20" />
              </div>
              <ChevronDown className="h-5 w-5 text-[#FCD116]/55 bounce-arrow" />
            </div>
          )}

          {/* ── ACROSS INDIA EXPANDED ── */}
          <div style={{ maxHeight: openFolder === "india" ? "6000px" : "0", overflow: "hidden", transition: "max-height 0.5s cubic-bezier(0.4,0,0.2,1)" }}>
            <div className="border border-[#FCD116]/20 bg-[#0F1014] p-6 mb-4">
              {/* State pills */}
              <div className="flex flex-wrap gap-2 mb-8">
                <span className="text-[9px] uppercase tracking-[0.2em] text-gray-500 font-bold self-center mr-1">Select State:</span>
                {INDIA_STATES.map(s => (
                  <button key={s.id} onClick={() => selectState(s.id)}
                    className={`text-xs font-bold py-1.5 px-4 transition-all duration-300 border ${openState === s.id ? "bg-[#FCD116] text-[#0B0C10] border-[#FCD116]" : "border-white/12 text-gray-300 hover:border-[#FCD116]/50 hover:text-white"}`}
                    data-testid={`state-pill-${s.id}`}>
                    {s.name}
                  </button>
                ))}
              </div>

              {/* Active state packages */}
              <div style={{ maxHeight: activeStateData ? "2000px" : "0", overflow: "hidden", transition: "max-height 0.45s ease" }}>
                {activeStateData && (
                  <div>
                    <div className="flex items-center gap-3 mb-6">
                      <div className="h-px w-8 bg-[#FCD116]" />
                      <h3 className="text-xl font-extrabold tracking-tight">{activeStateData.name}</h3>
                      <span className="text-[9px] uppercase tracking-widest text-gray-500 font-bold">{activeStateData.tagline}</span>
                      <div className="flex-1 h-px bg-white/5" />
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                      {activeStateData.packages.map(card => (
                        <PackageCard key={card.id} card={card} onEnquire={openModal} />
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* ── INTERNATIONAL COLLECTIONS EXPANDED ── */}
          <div style={{ maxHeight: openFolder === "international" ? "6000px" : "0", overflow: "hidden", transition: "max-height 0.5s cubic-bezier(0.4,0,0.2,1)" }}>
            <div className="border border-[#FCD116]/20 bg-[#0F1014] p-6 mb-4">
              {/* Continent pills */}
              <div className="flex flex-wrap gap-2 mb-8">
                <span className="text-[9px] uppercase tracking-[0.2em] text-gray-500 font-bold self-center mr-1">Select Region:</span>
                {INTL_CONTINENTS.map(c => (
                  <button key={c.id} onClick={() => selectContinent(c.id)}
                    className={`text-xs font-bold py-1.5 px-4 transition-all duration-300 border ${openContinent === c.id ? "bg-[#FCD116] text-[#0B0C10] border-[#FCD116]" : "border-white/12 text-gray-300 hover:border-[#FCD116]/50 hover:text-white"}`}
                    data-testid={`continent-pill-${c.id}`}>
                    {c.name}
                  </button>
                ))}
              </div>

              {/* Active continent packages */}
              <div style={{ maxHeight: activeContinentData ? "2000px" : "0", overflow: "hidden", transition: "max-height 0.45s ease" }}>
                {activeContinentData && (
                  <div>
                    <div className="flex items-center gap-3 mb-6">
                      <div className="h-px w-8 bg-[#FCD116]" />
                      <h3 className="text-xl font-extrabold tracking-tight">{activeContinentData.name}</h3>
                      <span className="text-[9px] uppercase tracking-widest text-gray-500 font-bold">{activeContinentData.tagline}</span>
                      <div className="flex-1 h-px bg-white/5" />
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                      {activeContinentData.packages.map(card => (
                        <PackageCard key={card.id} card={card} onEnquire={openModal} />
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════════
          VIVI PHILOSOPHY
      ══════════════════════════════════════════════════════════════ */}
      <section id="philosophy" ref={philRef}
        className={`bg-[#F9F8F6] text-[#0B0C10] border-y border-black/10 transition-all duration-1000 delay-100 ${philVis ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}
        style={{ paddingTop: "100px", paddingBottom: "100px" }}>
        <div className="max-w-7xl mx-auto px-6">
          <div className="max-w-3xl mb-20">
            <span className="text-[10px] uppercase tracking-[0.25em] text-[#0B0C10] font-extrabold mb-3 block border-l-4 border-[#FCD116] pl-3">Our Story</span>
            <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tight text-[#0B0C10] mb-6">
              15 Years. One Simple Philosophy.
            </h2>
            <p className="text-[#0B0C10]/75 text-base sm:text-lg font-light leading-relaxed">
              VIVI HOLIDAYS was built by people who lived in the travel industry — not observers of it. Over 15 years in tourism and hospitality taught us one thing: great travel comes down to who you know on the ground, how wide your network reaches, and how well you understand the person travelling.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12 lg:gap-16 pt-10 border-t border-black/10">
            {[
              { icon: Award, title: "On-Ground Expertise", body: "Fifteen years spent on the ground — not behind a desk — across India and internationally. We know the routes, the seasons, the local operators worth trusting, and the ones to avoid. That institutional knowledge is baked into every itinerary we build.", testid: "trust-card-1" },
              { icon: Globe, title: "Vast Vendor Network", body: "From boutique hill lodges in Himachal to private camps in the Serengeti, our handpicked partner network spans destinations across 5 continents. These are relationships built over years — which means better rooms, better guides, and better value passed directly to you.", testid: "trust-card-2" },
              { icon: Heart, title: "Personal Rapport", body: "Every enquiry at VIVI HOLIDAYS is handled by a real person who remembers your travel style, your preferences, and your story. We don't process bookings — we build journeys. That personal connection is what keeps our travellers coming back, and what they remember most.", testid: "trust-card-3" }
            ].map(({ icon: Icon, title, body, testid }) => (
              <div key={testid} className="text-left" data-testid={testid}>
                <div className="h-12 w-12 border border-black/20 flex items-center justify-center mb-6 bg-white shadow-sm">
                  <Icon className="h-6 w-6 text-[#0B0C10]" />
                </div>
                <h3 className="text-xl font-bold tracking-tight text-[#0B0C10] mb-4">{title}</h3>
                <p className="text-sm text-[#0B0C10]/70 leading-relaxed font-light">{body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════════
          GUEST DIARIES
      ══════════════════════════════════════════════════════════════ */}
      <section id="guest-diaries" ref={diaryRef}
        className={`py-28 max-w-7xl mx-auto px-6 border-b border-white/5 transition-all duration-1000 delay-100 ${diaryVis ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
        <div className="text-center max-w-2xl mx-auto mb-16">
          <span className="text-[10px] uppercase tracking-[0.2em] text-[#FCD116] font-bold mb-2 block">Real Reviews</span>
          <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tight">Guest Diaries</h2>
          <p className="text-gray-400 text-sm mt-3 font-light">Read unedited stories shared by travelers who experienced the unscripted freedom of VIVI.</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {[
            { quote: "The freedom of not having flights pre-bundled meant we used our credit card miles, yet had an elite driver and breathtaking villa completely arranged for us. Pure bliss.", author: "Aditi & Rohan", location: "Bali Escapade", stars: 5 },
            { quote: "As a solo female traveler in Vietnam, having access to the VIVI 24/7 Guardian Chat was incredibly reassuring. They literally rescheduled a street tour for me in 5 minutes.", author: "Priyanka S.", location: "Northern Vietnam Solo", stars: 5 },
            { quote: "Our group bike ride through Ladakh was magnificent. Having a professional mechanic following with a backup truck saved us twice. Exceptional local operations.", author: "Kabir's Motorcycle Caravan", location: "Ladakh High-Pass", stars: 5 }
          ].map((d, i) => (
            <div key={i} className="bg-[#13141A] border border-white/12 p-8 flex flex-col text-left hover:border-white/22 transition-colors">
              <div className="flex gap-1 mb-6">{[...Array(d.stars)].map((_, j) => <Star key={j} className="h-4 w-4 fill-[#FCD116] text-[#FCD116]" />)}</div>
              <p className="text-sm text-gray-300 italic mb-6 flex-grow leading-relaxed font-light">&ldquo;{d.quote}&rdquo;</p>
              <div>
                <span className="text-sm font-bold text-white block">{d.author}</span>
                <span className="text-xs text-gray-500 uppercase tracking-wide">{d.location}</span>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════════
          CONTACT & OFFICE
      ══════════════════════════════════════════════════════════════ */}
      <section id="contact" ref={contactRef}
        className={`py-24 px-6 border-b border-white/5 transition-all duration-1000 delay-100 ${contactVis ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}
        style={{ background: "#0B0C10" }}>
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            {/* Left: brand + CTA */}
            <div>
              <img src="https://customer-assets.emergentagent.com/job_ed8a34a7-db3c-4095-a429-13889e15a3bb/artifacts/hqhd52hw_VIVI.png"
                alt="VIVI HOLIDAYS" className="h-[55px] w-auto mb-8" />
              <span className="text-[10px] uppercase tracking-[0.3em] text-[#FCD116] font-bold mb-3 block">Reach Us</span>
              <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tight mb-6 leading-tight">
                Ready to Design Your<br />Perfect Journey?
              </h2>
              <p className="text-gray-400 text-sm font-light leading-relaxed mb-10 max-w-md">
                Our destination curators are ready to craft a bespoke travel experience tailored entirely around you. Flights excluded—total freedom included.
              </p>
              <button onClick={() => openModal("")}
                className="bg-[#FCD116] text-[#0B0C10] font-bold py-4 px-10 text-sm tracking-wide uppercase hover:bg-yellow-300 transition-all hover:scale-[1.02]"
                data-testid="contact-enquire-button">
                Start Your Journey
              </button>
            </div>

            {/* Right: contact details */}
            <div className="space-y-8">
              <div className="bg-[#13141A] border border-white/8 p-8">
                <div className="flex items-start gap-5 mb-8">
                  <div className="h-11 w-11 bg-[#FCD116]/10 border border-[#FCD116]/20 flex items-center justify-center shrink-0 mt-0.5">
                    <Phone className="h-5 w-5 text-[#FCD116]" />
                  </div>
                  <div>
                    <span className="text-[9px] uppercase tracking-[0.25em] text-gray-500 font-bold block mb-1">Phone & WhatsApp</span>
                    <a href="tel:07799949990" className="text-xl font-bold text-white hover:text-[#FCD116] transition-colors"
                      data-testid="contact-phone">
                      077999 49990
                    </a>
                    <p className="text-xs text-gray-500 mt-1 font-light">Mon–Sat, 10 AM – 8 PM IST</p>
                  </div>
                </div>
                <div className="h-px bg-white/6 mb-8" />
                <div className="flex items-start gap-5 mb-8">
                  <div className="h-11 w-11 bg-[#FCD116]/10 border border-[#FCD116]/20 flex items-center justify-center shrink-0 mt-0.5">
                    <Mail className="h-5 w-5 text-[#FCD116]" />
                  </div>
                  <div>
                    <span className="text-[9px] uppercase tracking-[0.25em] text-gray-500 font-bold block mb-1">Email</span>
                    <a href="mailto:hyd.viviholidays@outlook.com" className="text-base font-semibold text-white hover:text-[#FCD116] transition-colors"
                      data-testid="contact-email">
                      hyd.viviholidays@outlook.com
                    </a>
                  </div>
                </div>
                <div className="h-px bg-white/6 mb-8" />
                <div className="flex items-start gap-5">
                  <div className="h-11 w-11 bg-[#FCD116]/10 border border-[#FCD116]/20 flex items-center justify-center shrink-0 mt-0.5">
                    <MapPin className="h-5 w-5 text-[#FCD116]" />
                  </div>
                  <div>
                    <span className="text-[9px] uppercase tracking-[0.25em] text-gray-500 font-bold block mb-1.5">Office Address</span>
                    <address className="not-italic text-sm text-gray-300 leading-relaxed font-light" data-testid="contact-address">
                      Plot No. 105, First Floor, Road No. 10C,<br />
                      Gayathri Hills, Jubilee Hills,<br />
                      Lane between Med Plus and Apollo Pharmacy,<br />
                      Diamond House, Opposite Line,<br />
                      <span className="text-white font-medium">Hyderabad, Telangana 500082</span>
                    </address>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════════
          FOOTER
      ══════════════════════════════════════════════════════════════ */}
      <footer style={{ background: "#080909", borderTop: "1px solid rgba(255,255,255,0.06)" }} className="py-10 px-6">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-5">
          <img src="https://customer-assets.emergentagent.com/job_ed8a34a7-db3c-4095-a429-13889e15a3bb/artifacts/hqhd52hw_VIVI.png"
            alt="VIVI HOLIDAYS" className="h-[32px] w-auto" />
          <p className="text-xs text-gray-500 font-light text-center">© 2026 VIVI HOLIDAYS. Handcrafted Portfolios. All Rights Reserved. Flights excluded—total freedom included.</p>
          <button onClick={() => navigate("/admin")}
            className="text-xs text-gray-400 hover:text-[#FCD116] transition-colors uppercase tracking-wider font-semibold border border-white/10 px-4 py-2"
            data-testid="admin-access-button">
            Admin Access
          </button>
        </div>
      </footer>

      {/* ══════════════════════════════════════════════════════════════
          ENQUIRY MODAL
      ══════════════════════════════════════════════════════════════ */}
      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent data-testid="enquiry-modal" className="text-white max-w-lg p-8 rounded-none" style={{ background: "#0B0C10", border: "1px solid rgba(255,255,255,0.12)" }}>
          <DialogHeader className="text-left mb-4">
            <div className="flex justify-between items-start">
              <div>
                <span className="text-[10px] uppercase tracking-[0.25em] text-[#FCD116] font-bold block mb-1">Tailored Curation</span>
                <DialogTitle className="text-2xl font-extrabold tracking-tight text-white leading-tight" data-testid="modal-header-title">
                  Tailor This Portfolio with VIVI HOLIDAYS
                </DialogTitle>
              </div>
              <button onClick={() => setModalOpen(false)} className="text-gray-400 hover:text-white p-1 transition-colors" data-testid="close-inquiry-modal">
                <X className="h-5 w-5" />
              </button>
            </div>
            <DialogDescription className="text-gray-400 text-xs mt-2 font-light">
              Our curators will connect via WhatsApp to align dates, stays, and experiences.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            {[
              { label: "Full Name", name: "fullName", type: "text", icon: User, placeholder: "e.g. Siddhartha Malhotra", testid: "inquiry-full-name-input" },
              { label: "WhatsApp Contact", name: "whatsapp", type: "tel", icon: Phone, placeholder: "e.g. +91 9876543210", testid: "inquiry-whatsapp-number-input" },
              { label: "Desired Destination", name: "destination", type: "text", icon: MapPin, placeholder: "e.g. Bali & Nusa Penida", testid: "inquiry-destination-input" }
            ].map(({ label, name, type, icon: Icon, placeholder, testid }) => (
              <div key={name} className="space-y-1.5">
                <label className="text-[10px] uppercase tracking-wider text-gray-400 font-bold flex items-center gap-1.5">
                  <Icon className="h-3.5 w-3.5 text-[#FCD116]" /> {label}
                </label>
                <input type={type} name={name} required={name !== "destination"} value={formData[name]} onChange={handleFormChange}
                  placeholder={placeholder}
                  className="w-full px-4 py-2.5 text-sm focus:outline-none focus:border-[#FCD116] transition-colors"
                  style={{ background: "rgba(0,0,0,0.4)", border: "1px solid rgba(255,255,255,0.1)" }}
                  data-testid={testid} />
              </div>
            ))}
            <div className="space-y-1.5">
              <label className="text-[10px] uppercase tracking-wider text-gray-400 font-bold flex items-center gap-1.5">
                <Users className="h-3.5 w-3.5 text-[#FCD116]" /> Travelers
              </label>
              <div className="grid grid-cols-3 gap-2">
                {[{ label: "Solo", id: "Solo", testid: "traveler-toggle-solo" }, { label: "Couple", id: "Couple", testid: "traveler-toggle-couple" }, { label: "Small Group", id: "Small Group", testid: "traveler-toggle-group" }].map(o => (
                  <button key={o.id} type="button" onClick={() => setFormData(p => ({ ...p, travelers: o.id }))}
                    className={`text-xs py-2.5 font-semibold transition-all border ${formData.travelers === o.id ? "bg-[#FCD116] text-[#0B0C10] border-[#FCD116]" : "bg-black/30 text-gray-300 border-white/10 hover:border-white/30"}`}
                    data-testid={o.testid}>
                    {o.label}
                  </button>
                ))}
              </div>
            </div>
            <div className="space-y-1.5">
              <label className="text-[10px] uppercase tracking-wider text-gray-400 font-bold flex items-center gap-1.5">
                <Calendar className="h-3.5 w-3.5 text-[#FCD116]" /> Target Month
              </label>
              <div className="relative">
                <select name="targetMonth" value={formData.targetMonth} onChange={handleFormChange}
                  className="w-full px-4 py-2.5 text-sm focus:outline-none focus:border-[#FCD116] transition-colors appearance-none pr-10"
                  style={{ background: "rgba(0,0,0,0.4)", border: "1px solid rgba(255,255,255,0.1)" }}
                  data-testid="inquiry-target-month-select">
                  {["January","February","March","April","May","June","July","August","September","October","November","December"].map(m => (
                    <option key={m} value={`${m} 2026`} style={{ background: "#0B0C10" }}>{m} 2026</option>
                  ))}
                </select>
                <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 pointer-events-none text-gray-400" />
              </div>
            </div>
            <button type="submit" disabled={isSubmitting}
              className="w-full bg-[#FCD116] text-[#0B0C10] font-extrabold uppercase py-3.5 text-xs tracking-wider hover:bg-yellow-300 active:scale-[0.98] transition-all disabled:opacity-50"
              data-testid="submit-inquiry-button">
              {isSubmitting ? "Submitting..." : "Request Custom Quote & Itinerary"}
            </button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ─── Admin Dashboard ───────────────────────────────────────────────────────────
function ViviAdminDashboard() {
  const navigate = useNavigate();
  const [passwordInput, setPasswordInput] = useState("");
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [inquiries, setInquiries] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [token, setToken] = useState("");

  useEffect(() => {
    const t = sessionStorage.getItem("vivi_admin_token");
    if (t) { setToken(t); setIsLoggedIn(true); fetchInquiries(t); }
  }, []);

  const handleLoginSubmit = async (e) => {
    e.preventDefault();
    try {
      const res = await axios.post(`${API}/admin/login`, { username: "admin@viviholidays.com", password: passwordInput });
      const t = res.data.token;
      setToken(t); sessionStorage.setItem("vivi_admin_token", t);
      setIsLoggedIn(true); toast.success("Welcome back, architect."); fetchInquiries(t);
    } catch { toast.error("Invalid access code."); }
  };

  const fetchInquiries = async (t) => {
    setIsLoading(true);
    try { const r = await axios.get(`${API}/admin/enquiries`, { headers: { Authorization: `Bearer ${t}` } }); setInquiries(r.data); }
    catch (e) { toast.error("Failed to retrieve inquiries."); if (e.response?.status === 401) handleLogout(); }
    finally { setIsLoading(false); }
  };

  const handleStatusChange = async (id, newStatus) => {
    try {
      const r = await axios.put(`${API}/admin/enquiries/${id}`, { status: newStatus }, { headers: { Authorization: `Bearer ${token}` } });
      setInquiries(p => p.map(i => i.id === id ? { ...i, status: r.data.status } : i));
      toast.success(`Status updated to ${newStatus}`);
    } catch { toast.error("Failed to update status."); }
  };

  const handleDeleteInquiry = async (id) => {
    if (!window.confirm("Delete this enquiry?")) return;
    try {
      await axios.delete(`${API}/admin/enquiries/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      setInquiries(p => p.filter(i => i.id !== id)); toast.success("Deleted.");
    } catch { toast.error("Failed to delete."); }
  };

  const handleLogout = () => { sessionStorage.removeItem("vivi_admin_token"); setToken(""); setIsLoggedIn(false); setInquiries([]); navigate("/"); };

  const totalCount = inquiries.length;
  const pendingCount = inquiries.filter(i => i.status === "pending").length;
  const contactedCount = inquiries.filter(i => i.status === "contacted").length;
  const totalTravelers = inquiries.reduce((s, i) => s + (i.travelers === "Solo" ? 1 : i.travelers === "Couple" ? 2 : 5), 0);

  if (!isLoggedIn) return (
    <div style={{ background: "#0B0C10" }} className="text-white min-h-screen flex flex-col justify-center items-center px-6">
      <div className="max-w-md w-full border border-white/10 p-8 text-center" style={{ background: "#13141A" }}>
        <img src="https://customer-assets.emergentagent.com/job_ed8a34a7-db3c-4095-a429-13889e15a3bb/artifacts/hqhd52hw_VIVI.png" alt="VIVI" className="h-[55px] mx-auto mb-6" />
        <h1 className="text-2xl font-extrabold tracking-tight mb-2">Secure Admin Access</h1>
        <p className="text-gray-400 text-xs mb-8 font-light">Authenticate using the master passcode to review portfolio leads.</p>
        <form onSubmit={handleLoginSubmit} className="space-y-4">
          <div className="text-left space-y-1.5">
            <label className="text-[10px] uppercase tracking-wider text-gray-400 font-bold">Passcode</label>
            <input type="password" value={passwordInput} onChange={e => setPasswordInput(e.target.value)} required placeholder="••••••••••••"
              className="w-full px-4 py-3 text-sm focus:outline-none focus:border-[#FCD116] text-center tracking-widest"
              style={{ background: "rgba(0,0,0,0.5)", border: "1px solid rgba(255,255,255,0.1)" }}
              data-testid="admin-password-input" />
          </div>
          <button type="submit" className="w-full bg-[#FCD116] text-[#0B0C10] font-bold py-3.5 text-xs uppercase tracking-wider hover:bg-yellow-300 transition-all" data-testid="admin-login-submit">Verify Passcode</button>
          <button type="button" onClick={() => navigate("/")} className="text-xs text-gray-500 hover:text-white transition-colors pt-2 block mx-auto">Back to Live Site</button>
        </form>
      </div>
    </div>
  );

  return (
    <div style={{ background: "#0B0C10" }} className="text-white min-h-screen p-6 sm:p-10">
      <div className="max-w-7xl mx-auto space-y-8">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 pb-6 border-b border-white/10">
          <div className="flex items-center gap-3">
            <img src="https://customer-assets.emergentagent.com/job_ed8a34a7-db3c-4095-a429-13889e15a3bb/artifacts/hqhd52hw_VIVI.png" alt="VIVI" className="h-[40px]" />
            <div className="border-l border-white/20 pl-3">
              <span className="text-xs uppercase tracking-[0.2em] text-[#FCD116] font-bold block">D2C Travel Platform</span>
              <h1 className="text-xl font-extrabold tracking-tight">Admin Portal</h1>
            </div>
          </div>
          <div className="flex gap-3">
            <button onClick={() => fetchInquiries(token)} className="border border-white/10 px-4 py-2 text-xs font-semibold hover:bg-white/5 transition-colors uppercase tracking-wider">Refresh</button>
            <button onClick={handleLogout} className="bg-red-950/40 border border-red-900/50 text-red-400 px-4 py-2 text-xs font-semibold hover:bg-red-950 transition-colors uppercase tracking-wider flex items-center gap-1.5" data-testid="admin-logout-button">
              <LogOut className="h-3.5 w-3.5" /> Logout
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {[
            { icon: TrendingUp, label: "Total Enquiries", value: totalCount, color: "text-[#FCD116]", bg: "bg-[#FCD116]/10", testid: "admin-metric-total" },
            { icon: Clock, label: "Pending Status", value: pendingCount, color: "text-yellow-400", bg: "bg-yellow-950/20", testid: "admin-metric-pending" },
            { icon: Check, label: "Contacted/Closed", value: contactedCount, color: "text-green-400", bg: "bg-green-950/20" },
            { icon: Users, label: "Projected Travelers", value: totalTravelers, color: "text-[#FCD116]", bg: "bg-[#FCD116]/10" }
          ].map(({ icon: Icon, label, value, color, bg, testid }) => (
            <div key={label} className="border border-white/10 p-6 flex items-center gap-4" style={{ background: "#13141A" }}>
              <div className={`p-3 ${bg} ${color}`}><Icon className="h-6 w-6" /></div>
              <div>
                <span className="text-[10px] uppercase tracking-wider text-gray-500 block font-bold">{label}</span>
                <span className="text-2xl font-extrabold" data-testid={testid}>{value}</span>
              </div>
            </div>
          ))}
        </div>

        <div className="border border-white/10 overflow-hidden" style={{ background: "#13141A" }}>
          <div className="p-6 border-b border-white/10 flex justify-between items-center">
            <h2 className="text-lg font-bold tracking-tight">Active Customer Leads</h2>
            <span className="text-xs text-gray-400 bg-white/5 px-2.5 py-1">MongoDB Live</span>
          </div>
          {isLoading ? (
            <div className="py-20 text-center text-gray-400">
              <div className="animate-spin h-8 w-8 border-2 border-[#FCD116] border-t-transparent rounded-full mx-auto mb-4" />
              Retrieving...
            </div>
          ) : inquiries.length === 0 ? (
            <div className="py-20 text-center text-gray-400 font-light text-sm">No inquiries yet. Submissions appear here instantly.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse" data-testid="admin-inquiries-table">
                <thead>
                  <tr className="border-b border-white/10 text-[10px] uppercase tracking-wider text-gray-400 bg-black/20">
                    {["Timestamp","Client","Itinerary","Party","Notification","Status","Delete"].map(h => (
                      <th key={h} className="py-4 px-6 font-bold">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/5 text-sm">
                  {inquiries.map(item => (
                    <tr key={item.id} className="hover:bg-white/5 transition-colors" data-testid={`inquiry-row-${item.id}`}>
                      <td className="py-4 px-6 text-xs text-gray-400">{item.timestamp ? new Date(item.timestamp).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" }) : "N/A"}</td>
                      <td className="py-4 px-6">
                        <span className="font-bold text-white block">{item.full_name}</span>
                        <a href={`https://wa.me/${item.whatsapp_number.replace(/[^0-9]/g, "")}`} target="_blank" rel="noreferrer" className="text-xs text-[#FCD116] hover:underline flex items-center gap-1 mt-1">
                          <Phone className="h-3 w-3" /> {item.whatsapp_number}
                        </a>
                      </td>
                      <td className="py-4 px-6 font-medium text-gray-200">{item.destination}</td>
                      <td className="py-4 px-6 text-xs text-gray-300">
                        <span className="bg-white/5 border border-white/10 px-2.5 py-1 block w-fit">{item.travelers} &bull; {item.target_month}</span>
                      </td>
                      <td className="py-4 px-6 text-xs">
                        {item.email_notified ? <span className="text-green-400 flex items-center gap-1"><Check className="h-3.5 w-3.5" /> Sent</span> : <span className="text-gray-400">Pending</span>}
                      </td>
                      <td className="py-4 px-6">
                        <select value={item.status} onChange={e => handleStatusChange(item.id, e.target.value)}
                          className={`text-xs font-bold py-1 px-3 border appearance-none focus:outline-none ${item.status === "pending" ? "bg-yellow-950/30 border-yellow-700/50 text-yellow-400" : item.status === "contacted" ? "bg-green-950/30 border-green-700/50 text-green-400" : "bg-gray-900 border-white/15 text-gray-400"}`}
                          data-testid={`status-select-${item.id}`}>
                          <option value="pending" style={{ background: "#0B0C10" }}>Pending</option>
                          <option value="contacted" style={{ background: "#0B0C10" }}>Contacted</option>
                          <option value="closed" style={{ background: "#0B0C10" }}>Closed</option>
                        </select>
                      </td>
                      <td className="py-4 px-6 text-right">
                        <button onClick={() => handleDeleteInquiry(item.id)} className="text-gray-500 hover:text-red-400 p-2 transition-colors" data-testid={`delete-inquiry-button-${item.id}`}>
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── App Root ─────────────────────────────────────────────────────────────────
function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<ViviLandingPage />} />
          <Route path="/admin" element={<ViviAdminDashboard />} />
        </Routes>
      </BrowserRouter>
      <Toaster position="top-right" theme="dark" />
    </div>
  );
}

export default App;
